import { Box, Text, useDisclosure, VStack } from '@chakra-ui/react';
import { AccordionPanel } from '@gamma/ui/layout';
import { useContext, useEffect, useState } from 'react';
import { DetectionDetailsGrid, MitreTechniquesGrid } from '..';
import { i18n } from '../../../../shared/localization';
import { AuthContext } from '../../../../shared/providers/AuthProvider';
import { ChatGPTPromptCloudPanel } from '../../../entities/components';
import { DetectionQueryVariables, SantizedDetection } from '../../pages';
import {
  useGetAlertMetadata,
  useGetAlertMetadataSuricataRuleDescription,
  useGetDetections,
} from '../../queries';
import { DetectionDrawerEntityEDRPanel, SummaryPanel } from './Panels';
import { useDetectionContext } from '../../context';
import { Detection } from '@investigator/shared/generated/graphql';

const { detection: detectionText } = i18n.pages.detections;
const { chatGPT } = i18n.pages.integrations;

interface DetectionDrawerProps {
  isRelated?: boolean;
  disableBorders?: boolean;
  item: Detection | SantizedDetection | undefined;
  setDetectionData?: React.Dispatch<
    React.SetStateAction<Detection | SantizedDetection | undefined>
  >;
  queryDetectionsPaginatedVariables: DetectionQueryVariables;
  refetchMain?: boolean;
}

export const DetectionDrawer = ({
  item,
  isRelated,
  disableBorders = false,
  queryDetectionsPaginatedVariables,
  refetchMain,
}: DetectionDrawerProps) => {
  const { userPrivileges, isOrgTenant, loadPrivilegesForTenant } =
    useContext(AuthContext);

  const { setDetectionData } = useDetectionContext();

  const disclosure = useDisclosure();
  const [panelDescription, setPanelDescription] = useState<string>('');

  const { detection_id, tenant_info } = item || {};
  const { tenant_id = '' } = tenant_info || {};

  const { data: latestDetectionData } = useGetDetections({
    skip: !detection_id || !tenant_id,
    variables: {
      detection_ids: [
        {
          detection_id,
          tenant: tenant_id,
        },
      ],
    },
    fetchPolicy: 'cache-and-network', // Use cache-and-network to ensure updates are reflected
    notifyOnNetworkStatusChange: true, // Ensure component re-renders when refetch completes
  });

  const activeItem =
    latestDetectionData?.getDetections?.detections?.[0] || item;

  const activeDetection = activeItem;
  const { detection_id: activeDetectionId, tenant_info: activeTenantInfo } =
    activeDetection || {};
  const { tenant_id: activeTenantId = '' } = activeTenantInfo || {};

  // ORG view: if we don't yet have privileges for this member tenant, load them
  useEffect(() => {
    const tid = activeTenantId;
    if (
      isOrgTenant &&
      tid &&
      userPrivileges?.[tid] === undefined &&
      loadPrivilegesForTenant
    ) {
      loadPrivilegesForTenant(tid).catch((e: unknown) => {
        // Type-safe logging: surface Error.message when present
        const msg = e instanceof Error ? e.message : String(e);
        console.warn(
          '[Detections] failed to load member privileges for',
          tid,
          msg,
        );
      });
    }
  }, [isOrgTenant, activeTenantId, userPrivileges, loadPrivilegesForTenant]);

  // tri-state for privilege: true | false | null
  const rawGPT = userPrivileges?.[activeTenantId ?? '']?.chatgpt;
  const isGPTEnabled: boolean | null =
    rawGPT === undefined ? null : Boolean(rawGPT);

  const { alertCategory, significance, mitreTechniques } = detectionText;

  const getDetectionVariables = {
    detection_ids: [
      {
        detection_id: activeDetectionId,
        tenant: activeTenantInfo?.tenant_id ?? undefined,
      },
    ],
  };

  const {
    rank,
    alert_info,
    update_info,
    assignment_info,
    detection_status,
    total_alert_count,
    earliest_start_timestamp,
    latest_start_timestamp,
  } = activeItem || {};

  const {
    loading: metadataLoading,
    data: metadataData,
    error: _metadataError,
  } = useGetAlertMetadata({
    skip: !activeTenantInfo?.tenant_id || !alert_info?.content_id,
    variables: {
      items: [
        {
          content_id: alert_info?.content_id,
          tenant: activeTenantInfo?.tenant_id,
        },
      ],
    },
  });

  const {
    description,
    content_doc,
    mitre_mappings: metadata_mitre_mappings,
    rule,
  } = metadataData?.getAlertMetadata?.metadata?.[0] || {};

  const {
    data: suricataAlertDescriptionData,
    loading: suricataAlertDescriptionLoading,
    error: suricataAlertDescriptionError,
  } = useGetAlertMetadataSuricataRuleDescription({
    skip:
      isGPTEnabled !== true ||
      !activeTenantInfo?.tenant_id ||
      !alert_info?.content_id ||
      metadataLoading ||
      alert_info?.alert_type !== 'suricata_corelight',
    variables: {
      items: [
        {
          content_id: alert_info?.content_id,
          tenant: activeTenantInfo?.tenant_id,
        },
      ],
    },
  });

  useEffect(() => {
    // Reset description immediately when item changes to prevent showing old description
    setPanelDescription('');
  }, [item]);

  useEffect(() => {
    if (
      alert_info?.alert_type === 'suricata_corelight' &&
      suricataAlertDescriptionData?.getAlertMetadata?.metadata?.[0]
        ?.chatbot_chats?.suri_describe_rule
    ) {
      setPanelDescription(
        suricataAlertDescriptionData?.getAlertMetadata?.metadata?.[0]
          ?.chatbot_chats?.suri_describe_rule ?? '',
      );
    }

    if (alert_info?.alert_type !== 'suricata_corelight' && description) {
      setPanelDescription(description ?? '');
    }
  }, [
    suricataAlertDescriptionData,
    metadataData,
    alert_info?.alert_type,
    description,
  ]);

  useEffect(() => {
    if (activeDetectionId) {
      disclosure.onOpen();
    }
  }, [activeDetectionId, disclosure]);

  useEffect(() => {
    if (setDetectionData) {
      setDetectionData(activeItem);
    }
  }, [activeItem, setDetectionData]);

  const hasMetaContent = !!(description || content_doc?.significance);

  const showLeft = Boolean(
    (alert_info?.alert_type === 'suricata_corelight' && rule) ||
      (alert_info?.alert_type !== 'suricata_corelight' && description) ||
      (hasMetaContent && content_doc?.significance),
  );

  const sourceEntitiesRaw =
    activeItem?.paginated_source_entities?.alert_entities;
  const sourceEntities =
    sourceEntitiesRaw?.filter((e): e is NonNullable<typeof e> => {
      return e != null;
    }) ?? undefined;
  const totalSourceEntities =
    activeItem?.paginated_source_entities?.total_items ?? undefined;
  const destinationEntitiesRaw =
    activeItem?.paginated_destination_entities?.alert_entities;
  const destinationEntities =
    destinationEntitiesRaw?.filter(
      (e): e is NonNullable<typeof e> => e != null,
    ) ?? undefined;
  const totalDestinationEntities =
    activeItem?.paginated_destination_entities?.total_items ?? undefined;

  return (
    <VStack
      width="100%"
      spacing={!disableBorders ? 2 : 0}
      maxH="100vh"
      overflowY="auto"
    >
      {/* hide the left column if notice because there's no content to display */}
      {showLeft && (
        <VStack
          w="100%"
          spacing={!disableBorders ? 2 : 0}
          divider={
            disableBorders ? (
              <Box h="1px" w="100%" backgroundColor="border.layer.2" />
            ) : undefined
          }
          borderBottomWidth={disableBorders ? '1px' : undefined}
          borderBottomColor={disableBorders ? 'border.layer.2' : undefined}
        >
          {/* Ask GPT for suricata alerts */}
          {alert_info?.alert_type === 'suricata_corelight' && rule && (
            <ChatGPTPromptCloudPanel
              isAccordion={true}
              isDescriptionQuickView={true}
              titleText={chatGPT.description}
              disableBorders={disableBorders}
              descriptionText={panelDescription}
              isLoadingDescription={
                !panelDescription && suricataAlertDescriptionLoading
              }
              // must be boolean; coerce tri-state
              isGPTEnabled={isGPTEnabled !== false}
              loadingDescriptionError={suricataAlertDescriptionError}
              storageKey="gpt-prompt-panel-suri-details"
              showGPTDisabled={true}
            />
          )}
          {alert_info?.alert_type !== 'suricata_corelight' && description && (
            <AccordionPanel
              w="100%"
              layerStyle="first"
              reduceMotion={true}
              title={chatGPT.description}
              disableBorders={disableBorders}
              storageKey="gpt-prompt-cloud-panel"
            >
              <Text data-testid="detection-description" textStyle="body-md">
                {description}
              </Text>
            </AccordionPanel>
          )}
          {hasMetaContent && content_doc?.significance && (
            <AccordionPanel
              w="100%"
              layerStyle="first"
              reduceMotion={true}
              title={significance}
              disableBorders={disableBorders}
              storageKey="detection-significance-panel"
            >
              <Text data-testid="detection-significance" textStyle="body-md">
                {content_doc?.significance}
              </Text>
            </AccordionPanel>
          )}
        </VStack>
      )}
      <VStack
        w="100%"
        spacing={!disableBorders ? 2 : 0}
        divider={
          disableBorders ? (
            <Box h="1px" w="100%" backgroundColor="border.layer.2" />
          ) : undefined
        }
        borderBottomWidth={disableBorders ? '1px' : undefined}
        borderBottomColor={disableBorders ? 'border.layer.2' : undefined}
      >
        {activeItem && assignment_info && activeDetectionId && update_info && (
          <SummaryPanel
            detectionInfo={activeItem}
            refetchMain={refetchMain}
            disableBorders={disableBorders}
            assignmentInfo={assignment_info}
            detectionStatus={detection_status}
            getDetectionVariables={getDetectionVariables}
            queryDetectionsPaginatedVariables={
              queryDetectionsPaginatedVariables
            }
            total_alert_count={total_alert_count}
            earliest_start_timestamp={earliest_start_timestamp ?? undefined}
            latest_start_timestamp={latest_start_timestamp ?? undefined}
          />
        )}
        <DetectionDrawerEntityEDRPanel
          isRelated={isRelated}
          refetchMain={refetchMain}
          detectionInfo={activeItem as SantizedDetection | undefined}
          disableBorders={disableBorders}
          sourceEntities={sourceEntities}
          totalSourceEntities={totalSourceEntities}
          destinationEntities={destinationEntities}
          totalDestinationEntities={totalDestinationEntities}
        />
        <AccordionPanel
          w="full"
          layerStyle="first"
          reduceMotion={true}
          title={alertCategory}
          disableBorders={disableBorders}
          storageKey="detection-alert-category-panel"
        >
          {alert_info && rank && (
            <DetectionDetailsGrid alert_info={alert_info} rank={rank} />
          )}
        </AccordionPanel>
        {!!metadata_mitre_mappings?.tactics?.length && (
          <AccordionPanel
            w="full"
            layerStyle="first"
            reduceMotion={true}
            title={mitreTechniques}
            disableBorders={disableBorders}
            storageKey="detection-mitre-techs-panel"
          >
            <MitreTechniquesGrid tactics={metadata_mitre_mappings?.tactics} />
          </AccordionPanel>
        )}
      </VStack>
    </VStack>
  );
};
